import { useState } from "react";
import type { Dimensioned } from "@reno-estimator/core";
import {
  KITCHEN_SIZE_RANGES,
  kitchenSizeRangeForSqft,
  BASEMENT_SIZE_RANGES,
  basementSizeRangeForSqft,
  ADDITION_SIZE_RANGES,
  additionSizeRangeForSqft
} from "@reno-estimator/config";
import { StepShell } from "../ui/StepShell.js";
import { SliderField } from "../ui/SliderField.js";
import { OptionCard } from "../ui/OptionCard.js";
import { defaultRoomSqft } from "../../lib/defaults.js";
import type { EstimatorFlow } from "../../state/useEstimatorFlow.js";

/**
 * Kitchen gets a bucketed size question (per the Phase 5A brief - "Under
 * 80 sq. ft.", "80-120 sq. ft.", etc.) instead of the exact-sqft slider
 * every other project type uses, since most people renovating a kitchen
 * think in rough size categories, not square footage. The "Know the
 * exact size?" toggle below reveals the same slider used elsewhere -
 * per the brief, this bucketed question is explicitly "structured so
 * these ranges can later be replaced or supplemented with an exact
 * square-foot input" (see @reno-estimator/config's
 * kitchen-size-ranges.ts), and this toggle is that supplement: both
 * paths only ever produce a `room_size` answer, which is all the
 * calculation engine has ever accepted.
 */
function KitchenSizeStep({ flow }: { flow: EstimatorFlow }) {
  const roomSize = flow.getAnswer("room_size") as Dimensioned | undefined;
  const sizeRangeAnswer = flow.getAnswer("kitchen_size_range") as string | undefined;
  const [exactMode, setExactMode] = useState(false);

  function pickRange(rangeId: string) {
    const range = KITCHEN_SIZE_RANGES.find((r) => r.id === rangeId);
    if (!range) return;
    flow.setAnswer("kitchen_size_range", rangeId);
    flow.setAnswer("room_size", { value: range.representativeSqft, unit: "sqft" });
  }

  function pickExact(value: number) {
    flow.setAnswer("room_size", { value, unit: "sqft" });
    const matchingRange = kitchenSizeRangeForSqft(value);
    if (matchingRange) flow.setAnswer("kitchen_size_range", matchingRange.id);
  }

  return (
    <StepShell title="How large is the kitchen?" helpText="A rough range is fine to get started." onBack={flow.goBack} onNext={flow.goNext}>
      {!exactMode ? (
        <>
          <div className="option-grid" role="radiogroup" aria-label="Kitchen size">
            {KITCHEN_SIZE_RANGES.map((range) => (
              <OptionCard
                key={range.id}
                name="kitchen_size_range"
                value={range.id}
                label={range.label}
                checked={sizeRangeAnswer === range.id}
                onChange={() => pickRange(range.id)}
              />
            ))}
          </div>
          <button type="button" className="btn btn--link" onClick={() => setExactMode(true)}>
            Know the exact size? Enter it directly
          </button>
        </>
      ) : (
        <>
          <SliderField
            label="Exact size"
            unitLabel="sq. ft."
            min={0}
            max={1000}
            step={5}
            value={roomSize?.value ?? defaultRoomSqft(flow.projectType)}
            onChange={pickExact}
            helpText="This replaces the size range above with a precise figure - the estimate uses this exact value."
          />
          <button type="button" className="btn btn--link" onClick={() => setExactMode(false)}>
            Use a size range instead
          </button>
        </>
      )}
    </StepShell>
  );
}

/**
 * Basement gets the same bucketed-size treatment as kitchen (Phase 5A) -
 * see kitchen-size-ranges.ts's doc comment for the reasoning, which
 * applies identically here via basement-size-ranges.ts. Both project
 * types share the same "pick a range, or enter an exact number" pattern;
 * this is a separate component (rather than a parameterized one) since
 * the two size-range tables live in different config modules and a
 * future project type may want a genuinely different UI here.
 */
function BasementSizeStep({ flow }: { flow: EstimatorFlow }) {
  const roomSize = flow.getAnswer("room_size") as Dimensioned | undefined;
  const sizeRangeAnswer = flow.getAnswer("basement_size_range") as string | undefined;
  const [exactMode, setExactMode] = useState(false);

  function pickRange(rangeId: string) {
    const range = BASEMENT_SIZE_RANGES.find((r) => r.id === rangeId);
    if (!range) return;
    flow.setAnswer("basement_size_range", rangeId);
    flow.setAnswer("room_size", { value: range.representativeSqft, unit: "sqft" });
  }

  function pickExact(value: number) {
    flow.setAnswer("room_size", { value, unit: "sqft" });
    const matchingRange = basementSizeRangeForSqft(value);
    if (matchingRange) flow.setAnswer("basement_size_range", matchingRange.id);
  }

  return (
    <StepShell title="How large is the basement?" helpText="A rough range is fine to get started." onBack={flow.goBack} onNext={flow.goNext}>
      {!exactMode ? (
        <>
          <div className="option-grid" role="radiogroup" aria-label="Basement size">
            {BASEMENT_SIZE_RANGES.map((range) => (
              <OptionCard
                key={range.id}
                name="basement_size_range"
                value={range.id}
                label={range.label}
                checked={sizeRangeAnswer === range.id}
                onChange={() => pickRange(range.id)}
              />
            ))}
          </div>
          <button type="button" className="btn btn--link" onClick={() => setExactMode(true)}>
            Know the exact size? Enter it directly
          </button>
        </>
      ) : (
        <>
          <SliderField
            label="Exact size"
            unitLabel="sq. ft."
            min={0}
            max={3000}
            step={25}
            value={roomSize?.value ?? defaultRoomSqft(flow.projectType)}
            onChange={pickExact}
            helpText="This replaces the size range above with a precise figure - the estimate uses this exact value."
          />
          <button type="button" className="btn btn--link" onClick={() => setExactMode(false)}>
            Use a size range instead
          </button>
        </>
      )}
    </StepShell>
  );
}

/**
 * Addition gets the same bucketed-size treatment as kitchen/basement -
 * see kitchen-size-ranges.ts's doc comment for the reasoning, which
 * applies identically here via addition-size-ranges.ts (Phase 12).
 */
function AdditionSizeStep({ flow }: { flow: EstimatorFlow }) {
  const roomSize = flow.getAnswer("room_size") as Dimensioned | undefined;
  const sizeRangeAnswer = flow.getAnswer("addition_size_range") as string | undefined;
  const [exactMode, setExactMode] = useState(false);

  function pickRange(rangeId: string) {
    const range = ADDITION_SIZE_RANGES.find((r) => r.id === rangeId);
    if (!range) return;
    flow.setAnswer("addition_size_range", rangeId);
    flow.setAnswer("room_size", { value: range.representativeSqft, unit: "sqft" });
  }

  function pickExact(value: number) {
    flow.setAnswer("room_size", { value, unit: "sqft" });
    const matchingRange = additionSizeRangeForSqft(value);
    if (matchingRange) flow.setAnswer("addition_size_range", matchingRange.id);
  }

  return (
    <StepShell title="How large is the addition?" helpText="A rough range is fine to get started." onBack={flow.goBack} onNext={flow.goNext}>
      {!exactMode ? (
        <>
          <div className="option-grid" role="radiogroup" aria-label="Addition size">
            {ADDITION_SIZE_RANGES.map((range) => (
              <OptionCard
                key={range.id}
                name="addition_size_range"
                value={range.id}
                label={range.label}
                checked={sizeRangeAnswer === range.id}
                onChange={() => pickRange(range.id)}
              />
            ))}
          </div>
          <button type="button" className="btn btn--link" onClick={() => setExactMode(true)}>
            Know the exact size? Enter it directly
          </button>
        </>
      ) : (
        <>
          <SliderField
            label="Exact size"
            unitLabel="sq. ft."
            min={0}
            max={2500}
            step={25}
            value={roomSize?.value ?? defaultRoomSqft(flow.projectType)}
            onChange={pickExact}
            helpText="This replaces the size range above with a precise figure - the estimate uses this exact value."
          />
          <button type="button" className="btn btn--link" onClick={() => setExactMode(false)}>
            Use a size range instead
          </button>
        </>
      )}
    </StepShell>
  );
}

export function Step2RoomSize({ flow }: { flow: EstimatorFlow }) {
  if (flow.projectType === "kitchen") {
    return <KitchenSizeStep flow={flow} />;
  }
  if (flow.projectType === "basement" || flow.projectType === "basement_suite") {
    return <BasementSizeStep flow={flow} />;
  }
  if (flow.projectType === "addition") {
    return <AdditionSizeStep flow={flow} />;
  }

  const roomSize = (flow.getAnswer("room_size") as Dimensioned | undefined) ?? {
    value: defaultRoomSqft(flow.projectType),
    unit: "sqft" as const
  };

  return (
    <StepShell title="How large is the space?" helpText="A rough square footage is fine - drag the slider or type an exact number." onBack={flow.goBack} onNext={flow.goNext}>
      <SliderField
        label="Approximate size"
        unitLabel="sq. ft."
        min={0}
        max={3000}
        step={5}
        value={roomSize.value}
        onChange={(value) => flow.setAnswer("room_size", { value, unit: "sqft" })}
        helpText="Not sure of the exact number? An estimate is fine - you can always come back and adjust it."
      />
    </StepShell>
  );
}
